import {
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  useEffect,
} from 'react'
import { useDispatch } from 'react-redux'
import {
  type KibMultiSelectionOption,
  KibSuperSelect,
  type KibSuperSelectProps,
} from '@chewy/kib-fields-new-react'
import type { User } from '@pbt/pbt-ui-components'

import {
  useGetRoleIdsList,
  UserSelectFilterScope,
} from '~/components/common/inputs/UserSelect'
import SecretSearchContext from '~/constants/secretSearchContext'
import {
  clearDynamicSearchCache,
  fetchSuggestionResults,
} from '~/store/actions/search'
import { TeamFilter } from '~/types'
import useEffectExceptOnMount from '~/utils/useEffectExceptOnMount'

import '@chewy/kib-fields-new-styles/dist/kib-fields-new.css'
import '@chewy/kib-interstitials-styles/dist/styles/kib-spinner.css'

export interface KibUserSelectBaseProps {
  disabled?: boolean
  filterScope?: UserSelectFilterScope
  includeInactive?: boolean
  inputValue: string
  internalValue: KibMultiSelectionOption[]
  isLoading: boolean
  label?: string
  onBlur?: () => void
  onChange?: (e: string) => void
  onFocus?: () => void
  onQuery: (
    v?: string,
    e?:
      | ChangeEvent<HTMLInputElement>
      | ReactMouseEvent<HTMLElement>
      | FocusEvent<HTMLInputElement>
      | KeyboardEvent<HTMLInputElement>,
  ) => void
  placeHolder: string
  readOnly?: boolean
  required?: boolean
  reset?: boolean
  setInternalValue: (value: KibMultiSelectionOption[]) => void
  teamFilter?: TeamFilter
  useInMemory?: boolean
  userOptions: KibMultiSelectionOption[]
  userSelectStateId: string
  usersMap: Record<string, User>
}

export const KibUserSelectBase = ({
  reset = false,
  label,
  userSelectStateId,
  includeInactive = false,
  required = false,
  teamFilter = TeamFilter.MEMBER,
  placeHolder,
  disabled,
  onChange,
  readOnly,
  filterScope = UserSelectFilterScope.MainStaff,
  useInMemory,
  onBlur,
  onFocus,
  userOptions = [],
  isLoading,
  inputValue,
  onQuery,
  setInternalValue,
  internalValue,
  usersMap,
}: KibUserSelectBaseProps) => {
  const dispatch = useDispatch()
  const roleIdsList = useGetRoleIdsList(filterScope)
  const maxCount = useInMemory ? 50 : undefined

  const dispatchFetchSearch = (searchTerm: string) => {
    dispatch(
      fetchSuggestionResults({
        searchContext: SecretSearchContext.PERSONS,
        searchTerm,
        teamFilter,
        roleIds: roleIdsList.join(','),
        includeInactive,
        maxCount,
        shorten: true,
        stateId: userSelectStateId,
      }),
    )
  }

  useEffect(
    () => () => {
      dispatch(clearDynamicSearchCache(userSelectStateId))
    },
    [],
  )

  useEffectExceptOnMount(() => {
    if (inputValue) {
      dispatchFetchSearch(inputValue)
    }
  }, [inputValue])

  const setValues = (
    values: KibMultiSelectionOption[],
    ev: Parameters<NonNullable<KibSuperSelectProps['onChange']>>[1],
  ) => {
    if (ev?.type === 'blur') {
      return
    }

    const users = values.map((o) => usersMap[o.value]).filter(Boolean)

    if (users.length === 1) {
      if (onChange) {
        onChange(users[0].id)
      }
      setInternalValue(values)
    }
  }

  const onClear = () => {
    if (!reset) {
      onChange?.('')
    }
    setInternalValue([])
  }

  return (
    <KibSuperSelect
      isAsync
      disabled={disabled || readOnly}
      label={label || ''}
      loading={isLoading}
      options={userOptions}
      placeholder={placeHolder}
      required={required}
      value={internalValue}
      onBlur={onBlur}
      onChange={(data, event) => setValues(data, event)}
      onClear={onClear}
      onFocus={onFocus}
      onQuery={onQuery}
    />
  )
}
